// ============================================================
// PAYMENT ORCHESTRATION ENGINE — CANONICAL SOURCE
// ============================================================
//
// Extracted from corebank-platform.jsx for standalone testing
// and eventual production use.
//
// ARCHITECTURE:
//   A payment is a state machine:
//     INITIATED → VALIDATED → [PENDING_APPROVAL →] SUBMITTED → ACCEPTED → SETTLED
//                                                            → REJECTED
//                                              → FAILED
//                          → FAILED (validation failure)
//     SETTLED → RETURNED  (post-settlement return)
//
//   At each state transition, the orchestrator performs ledger operations:
//     INITIATED        → Place hold on sender's available balance
//     VALIDATED        → No ledger action (compliance/limit checks passed)
//     PENDING_APPROVAL → No ledger action (awaiting dual-auth)
//     SUBMITTED        → No ledger action (sent to scheme adapter)
//     ACCEPTED         → Capture hold, post settlement entries
//     REJECTED         → Release hold, no settlement
//     FAILED           → Release hold if placed
//     RETURNED         → Reverse settlement entries
//
// DEPENDS ON:
//   - LedgerEngine instance (constructor arg)
//   - EventBus (accessed via ledger.eventBus)
//   - AccountStatus, Direction from ledger types
//
// COMPONENTS:
//   1. PaymentOrchestrator — state machine, validation, settlement
//   2. BeneficiaryRegistry — saved payee management
//   3. StandingOrderEngine — recurring payment scheduling
//   4. Validators — pure-function validation pipeline
//   5. PaymentSchemeAdapter — simulated scheme submission (FPS/CHAPS/BACS/SWIFT)
//   6. Approval workflow — dual authorisation for above-threshold payments
//   7. Daily limit tracking — per-scheme daily outbound limits
//   8. Nostro reconciliation — correspondent bank position summary
//
// ============================================================

import { AccountStatus, Direction } from './ledger-engine.js';
import { businessDate as _defaultBusinessDate } from './business-date.js';

// ---- ID GENERATION ----
let _seq = 0;
function genId(pfx = '') {
  _seq++;
  return `${pfx}${Date.now().toString(36)}${_seq.toString(36).padStart(4, '0')}${Math.random().toString(36).slice(2, 6)}`;
}


// ============================================================
// PAYMENT TYPES & ENUMS
// ============================================================

const PaymentStatus = {
  INITIATED: 'INITIATED',
  VALIDATED: 'VALIDATED',
  PENDING_APPROVAL: 'PENDING_APPROVAL',
  SUBMITTED: 'SUBMITTED',
  ACCEPTED: 'ACCEPTED',
  SETTLED: 'SETTLED',
  REJECTED: 'REJECTED',
  FAILED: 'FAILED',
  RETURNED: 'RETURNED',
  REFERRED: 'REFERRED',
};

const PaymentDirection = {
  OUTBOUND: 'OUTBOUND',
  INBOUND: 'INBOUND',
};

const PaymentScheme = {
  FPS: 'FPS',
  CHAPS: 'CHAPS',
  BACS: 'BACS',
  SWIFT: 'SWIFT',
  INTERNAL: 'INTERNAL',
};

const PAYMENT_TRANSITIONS = {
  [PaymentStatus.INITIATED]: [PaymentStatus.VALIDATED, PaymentStatus.FAILED],
  [PaymentStatus.VALIDATED]: [PaymentStatus.PENDING_APPROVAL, PaymentStatus.SUBMITTED, PaymentStatus.FAILED],
  [PaymentStatus.PENDING_APPROVAL]: [PaymentStatus.SUBMITTED, PaymentStatus.FAILED, PaymentStatus.REFERRED],
  [PaymentStatus.REFERRED]: [PaymentStatus.PENDING_APPROVAL, PaymentStatus.FAILED],
  [PaymentStatus.SUBMITTED]: [PaymentStatus.ACCEPTED, PaymentStatus.REJECTED, PaymentStatus.FAILED],
  [PaymentStatus.ACCEPTED]: [PaymentStatus.SETTLED, PaymentStatus.RETURNED],
  [PaymentStatus.SETTLED]: [PaymentStatus.RETURNED],
  [PaymentStatus.REJECTED]: [],
  [PaymentStatus.FAILED]: [],
  [PaymentStatus.RETURNED]: [],
};


// ============================================================
// SCHEME CONFIGURATION
// ============================================================

const SCHEME_CONFIG = {
  [PaymentScheme.FPS]: {
    name: 'Faster Payments', maxAmount: 1000000, currency: 'GBP',
    holdExpiryHours: 2, typicalSettlementMs: 1500,
    requiresSortCode: true, requiresAccountNumber: true,
    cutoffTime: null,
    approvalThreshold: 50000,
  },
  [PaymentScheme.CHAPS]: {
    name: 'CHAPS', maxAmount: null, currency: 'GBP',
    holdExpiryHours: 24, typicalSettlementMs: 3000,
    requiresSortCode: true, requiresAccountNumber: true,
    cutoffTime: '15:30',
    approvalThreshold: 250000,
  },
  [PaymentScheme.BACS]: {
    name: 'BACS', maxAmount: 20000000, currency: 'GBP',
    holdExpiryHours: 72, typicalSettlementMs: 0,
    requiresSortCode: true, requiresAccountNumber: true,
    cutoffTime: '22:30',
    settlementDays: 3,
    approvalThreshold: 100000,
  },
  [PaymentScheme.SWIFT]: {
    name: 'SWIFT', maxAmount: null, currency: null,
    holdExpiryHours: 48, typicalSettlementMs: 5000,
    requiresBIC: true, requiresIBAN: true,
    cutoffTime: '15:00',
    approvalThreshold: 25000,
  },
  [PaymentScheme.INTERNAL]: {
    name: 'Internal Transfer', maxAmount: null, currency: null,
    holdExpiryHours: 0, typicalSettlementMs: 0,
    cutoffTime: null,
    approvalThreshold: null,
  },
};


// ============================================================
// APPROVAL CONFIGURATION
// ============================================================

const APPROVAL_CONFIG = {
  enabled: true,
  defaultThreshold: 50000,
  getThreshold: (scheme) => SCHEME_CONFIG[scheme]?.approvalThreshold ?? 50000,
  requiresDualAuth: (scheme, amount) => {
    if (scheme === PaymentScheme.INTERNAL) return false;
    const threshold = SCHEME_CONFIG[scheme]?.approvalThreshold;
    if (threshold === null || threshold === undefined) return false;
    return amount >= threshold;
  },
};


// ============================================================
// DAILY LIMITS
// ============================================================

const DAILY_LIMITS = {
  FPS: 5000000,
  CHAPS: 20000000,
  BACS: 10000000,
  SWIFT: 10000000,
  INTERNAL: null,
};


// ============================================================
// PAYMENT VALIDATION PIPELINE
// ============================================================
// Each validator is a pure function: (payment, context) → { valid, errors }
// The pipeline runs ALL validators and collects all errors.
// This is deliberate — we want to show the user everything
// wrong at once, not one error at a time.

const Validators = {
  amountPositive: (pmt) => {
    if (pmt.amount <= 0) return { valid: false, errors: ['Amount must be positive'] };
    return { valid: true, errors: [] };
  },

  amountWithinSchemeLimit: (pmt) => {
    const config = SCHEME_CONFIG[pmt.scheme];
    if (config && config.maxAmount && pmt.amount > config.maxAmount) {
      return { valid: false, errors: [`Exceeds ${pmt.scheme} limit of ${config.maxAmount.toLocaleString()}`] };
    }
    return { valid: true, errors: [] };
  },

  amountWithinProductLimit: (pmt, ctx) => {
    const product = ctx.product;
    if (!product) return { valid: true, errors: [] };
    if (product.limits?.singlePayment && pmt.amount > product.limits.singlePayment) {
      return { valid: false, errors: [`Exceeds single payment limit of ${product.limits.singlePayment.toLocaleString()}`] };
    }
    return { valid: true, errors: [] };
  },

  sufficientFunds: (pmt, ctx) => {
    if (pmt.direction !== PaymentDirection.OUTBOUND) return { valid: true, errors: [] };
    const balance = ctx.balance;
    if (!balance) return { valid: true, errors: [] };
    const odLimit = ctx.product?.overdraft?.allowed ? (ctx.product.overdraft.maxCommitted + (ctx.product.overdraft.maxUncommitted || 0)) : 0;
    const totalAvailable = balance.available + odLimit;
    const totalNeeded = pmt.amount + (ctx.feeAmount || 0);
    if (totalNeeded > totalAvailable) {
      return { valid: false, errors: [`Insufficient funds: need ${totalNeeded.toLocaleString()}, available ${totalAvailable.toLocaleString()}`] };
    }
    return { valid: true, errors: [] };
  },

  currencyMatch: (pmt) => {
    const config = SCHEME_CONFIG[pmt.scheme];
    if (config && config.currency && pmt.currency !== config.currency) {
      return { valid: false, errors: [`${pmt.scheme} only supports ${config.currency}, got ${pmt.currency}`] };
    }
    return { valid: true, errors: [] };
  },

  accountActive: (pmt, ctx) => {
    if (!ctx.sourceAccount) return { valid: true, errors: [] };
    if (ctx.sourceAccount.status !== AccountStatus.ACTIVE) {
      return { valid: false, errors: ['Source account is not active'] };
    }
    return { valid: true, errors: [] };
  },

  beneficiaryDetails: (pmt) => {
    const config = SCHEME_CONFIG[pmt.scheme];
    if (!config) return { valid: true, errors: [] };
    const errors = [];
    if (config.requiresSortCode && !pmt.beneficiary?.sortCode) errors.push('Sort code required');
    if (config.requiresAccountNumber && !pmt.beneficiary?.accountNumber) errors.push('Account number required');
    if (config.requiresBIC && !pmt.beneficiary?.bic) errors.push('BIC/SWIFT code required');
    if (config.requiresIBAN && !pmt.beneficiary?.iban) errors.push('IBAN required');
    if (!pmt.beneficiary?.name) errors.push('Beneficiary name required');
    return { valid: errors.length === 0, errors };
  },

  sortCodeFormat: (pmt) => {
    if (pmt.scheme === PaymentScheme.INTERNAL) return { valid: true, errors: [] };
    if (!pmt.beneficiary?.sortCode) return { valid: true, errors: [] };
    if (!/^\d{6}$/.test(pmt.beneficiary.sortCode.replace(/-/g, ''))) {
      return { valid: false, errors: ['Sort code must be 6 digits'] };
    }
    return { valid: true, errors: [] };
  },

  accountNumberFormat: (pmt) => {
    if (pmt.scheme === PaymentScheme.INTERNAL) return { valid: true, errors: [] };
    if (!pmt.beneficiary?.accountNumber) return { valid: true, errors: [] };
    if (!/^\d{8}$/.test(pmt.beneficiary.accountNumber)) {
      return { valid: false, errors: ['Account number must be 8 digits'] };
    }
    return { valid: true, errors: [] };
  },

  notDuplicate: (pmt, ctx) => {
    if (!ctx.recentPayments) return { valid: true, errors: [] };
    const dup = ctx.recentPayments.find(p =>
      p.amount === pmt.amount &&
      p.beneficiary?.accountNumber === pmt.beneficiary?.accountNumber &&
      p.beneficiary?.sortCode === pmt.beneficiary?.sortCode &&
      p.status !== PaymentStatus.FAILED &&
      p.status !== PaymentStatus.REJECTED &&
      (Date.now() - new Date(p.createdAt).getTime()) < 300000
    );
    if (dup) return { valid: false, errors: [`Possible duplicate of payment ${dup.id.slice(0, 12)}…`] };
    return { valid: true, errors: [] };
  },
};

function runValidationPipeline(payment, context) {
  const allErrors = [];
  const validators = [
    Validators.amountPositive,
    Validators.amountWithinSchemeLimit,
    Validators.amountWithinProductLimit,
    Validators.sufficientFunds,
    Validators.currencyMatch,
    Validators.accountActive,
    Validators.beneficiaryDetails,
    Validators.sortCodeFormat,
    Validators.accountNumberFormat,
    Validators.notDuplicate,
  ];
  for (const v of validators) {
    const result = v(payment, context);
    if (!result.valid) allErrors.push(...result.errors);
  }
  return { valid: allErrors.length === 0, errors: allErrors };
}


// ============================================================
// SCHEME ADAPTER (simulated)
// ============================================================
// In production: each scheme provider implements this interface.
// Modulr → FPS/CHAPS, ClearBank → FPS/CHAPS (alternative),
// SWIFT Alliance Lite2 → SWIFT.

class PaymentSchemeAdapter {
  constructor(scheme) {
    this.scheme = scheme;
    this.config = SCHEME_CONFIG[scheme];
    this.submissionLog = [];
    this._successRate = 0.9; // Configurable for testing
  }

  setSuccessRate(rate) { this._successRate = rate; }

  async submit(payment) {
    const delay = this.config.typicalSettlementMs || 0;
    const submission = {
      schemeReference: `${this.scheme}-${genId('')}`,
      submittedAt: new Date().toISOString(),
      paymentId: payment.id,
    };
    this.submissionLog.push(submission);

    return new Promise(resolve => {
      setTimeout(() => {
        const success = Math.random() < this._successRate;
        resolve({
          ...submission,
          status: success ? 'ACCEPTED' : 'REJECTED',
          respondedAt: new Date().toISOString(),
          rejectionReason: success ? null : 'Beneficiary account closed',
          schemeTransactionId: success ? `SCH-${genId('')}` : null,
        });
      }, Math.min(delay, 50)); // Cap at 50ms for testing
    });
  }
}


// ============================================================
// BENEFICIARY REGISTRY
// ============================================================
// Saved payees for quick payment initiation. In production this
// would include confirmation of payee (CoP) checks via Pay.UK.

class BeneficiaryRegistry {
  constructor() { this.beneficiaries = new Map(); }

  add({ name, sortCode, accountNumber, bic, iban, scheme, nickname, customerId, createdBy }) {
    const id = genId('ben_');
    const beneficiary = {
      id, name, sortCode: sortCode?.replace(/-/g, '') || null, accountNumber: accountNumber || null,
      bic: bic || null, iban: iban || null,
      scheme: scheme || (sortCode ? 'FPS' : bic ? 'SWIFT' : 'INTERNAL'),
      nickname: nickname || name, customerId: customerId || null,
      createdBy: createdBy || 'system', createdAt: new Date().toISOString(),
      lastUsed: null, useCount: 0, status: 'ACTIVE',
    };
    this.beneficiaries.set(id, beneficiary);
    return beneficiary;
  }

  get(id) { return this.beneficiaries.get(id); }

  getByCustomer(customerId) {
    return Array.from(this.beneficiaries.values()).filter(b => b.customerId === customerId && b.status === 'ACTIVE');
  }

  getAll() { return Array.from(this.beneficiaries.values()).filter(b => b.status === 'ACTIVE'); }

  recordUsage(id) {
    const b = this.beneficiaries.get(id);
    if (b) { b.lastUsed = new Date().toISOString(); b.useCount++; }
  }

  deactivate(id) {
    const b = this.beneficiaries.get(id);
    if (b) { b.status = 'INACTIVE'; }
    return b;
  }

  toBeneficiaryPayload(id) {
    const b = this.beneficiaries.get(id);
    if (!b) return null;
    const payload = { name: b.name };
    if (b.sortCode) payload.sortCode = b.sortCode;
    if (b.accountNumber) payload.accountNumber = b.accountNumber;
    if (b.bic) payload.bic = b.bic;
    if (b.iban) payload.iban = b.iban;
    return payload;
  }
}


// ============================================================
// STANDING ORDER ENGINE
// ============================================================
// Recurring payments executed on schedule. Each order carries
// its own beneficiary details, amount, frequency, and execution
// history. The EOD engine would call executeDue() daily.

const SOFrequency = { WEEKLY: 'WEEKLY', FORTNIGHTLY: 'FORTNIGHTLY', MONTHLY: 'MONTHLY', QUARTERLY: 'QUARTERLY', ANNUAL: 'ANNUAL' };
const SOStatus = { ACTIVE: 'ACTIVE', PAUSED: 'PAUSED', CANCELLED: 'CANCELLED', COMPLETED: 'COMPLETED' };

class StandingOrderEngine {
  constructor(orchestrator, options = {}) {
    this.orchestrator = orchestrator;
    this._businessDate = options.businessDate || _defaultBusinessDate;
    this.orders = new Map();
    this.executions = [];
  }

  _nowISO() { return this._businessDate.getCurrentDate().toISOString(); }

  create({ sourceAccountId, beneficiaryId, beneficiary, amount, currency, scheme, reference, frequency, startDate, endDate, maxExecutions, createdBy }) {
    const id = genId('so_');
    const order = {
      id, sourceAccountId, beneficiaryId: beneficiaryId || null,
      beneficiary: beneficiary || null,
      amount, currency, scheme: scheme || 'FPS',
      reference: reference || `SO-${id.slice(3, 10)}`,
      frequency: frequency || SOFrequency.MONTHLY,
      startDate: startDate || this._nowISO().split('T')[0],
      endDate: endDate || null,
      maxExecutions: maxExecutions || null,
      status: SOStatus.ACTIVE,
      executionCount: 0,
      nextExecutionDate: startDate || this._nowISO().split('T')[0],
      lastExecutedAt: null,
      createdBy: createdBy || 'system',
      createdAt: this._nowISO(),
      history: [],
    };
    this.orders.set(id, order);
    return order;
  }

  getAll() { return Array.from(this.orders.values()); }
  get(id) { return this.orders.get(id); }

  pause(id) {
    const o = this.orders.get(id);
    if (!o || o.status !== SOStatus.ACTIVE) throw new Error('Cannot pause: not active');
    o.status = SOStatus.PAUSED;
    return o;
  }

  resume(id) {
    const o = this.orders.get(id);
    if (!o || o.status !== SOStatus.PAUSED) throw new Error('Cannot resume: not paused');
    o.status = SOStatus.ACTIVE;
    return o;
  }

  cancel(id, reason) {
    const o = this.orders.get(id);
    if (!o || o.status === SOStatus.CANCELLED || o.status === SOStatus.COMPLETED) throw new Error('Cannot cancel');
    o.status = SOStatus.CANCELLED;
    o.history.push({ action: 'CANCELLED', at: this._nowISO(), reason });
    return o;
  }

  getDue(asOfDate) {
    const dateStr = typeof asOfDate === 'string' ? asOfDate : asOfDate.toISOString().split('T')[0];
    return Array.from(this.orders.values()).filter(o =>
      o.status === SOStatus.ACTIVE && o.nextExecutionDate <= dateStr
    );
  }

  async execute(orderId) {
    const order = this.orders.get(orderId);
    if (!order || order.status !== SOStatus.ACTIVE) throw new Error('Order not active');

    if (order.maxExecutions && order.executionCount >= order.maxExecutions) {
      order.status = SOStatus.COMPLETED;
      order.history.push({ action: 'COMPLETED', at: this._nowISO(), reason: 'Max executions reached' });
      return { success: false, reason: 'Max executions reached' };
    }

    if (order.endDate && this._nowISO().split('T')[0] > order.endDate) {
      order.status = SOStatus.COMPLETED;
      order.history.push({ action: 'COMPLETED', at: this._nowISO(), reason: 'End date passed' });
      return { success: false, reason: 'End date passed' };
    }

    try {
      const pmt = this.orchestrator.initiatePayment({
        sourceAccountId: order.sourceAccountId,
        amount: order.amount,
        currency: order.currency,
        scheme: order.scheme,
        beneficiary: order.beneficiary,
        reference: `${order.reference}-${order.executionCount + 1}`,
        metadata: { standingOrderId: order.id },
        initiatedBy: 'standing-order-engine',
      });

      const valResult = this.orchestrator.validatePayment(pmt.id);
      if (valResult.valid) {
        await this.orchestrator.submitPayment(pmt.id, { skipApproval: true });
      }

      const execution = {
        orderId: order.id, paymentId: pmt.id,
        executedAt: this._nowISO(),
        success: pmt.status === PaymentStatus.SETTLED,
        finalStatus: pmt.status,
      };
      this.executions.push(execution);

      order.executionCount++;
      order.lastExecutedAt = execution.executedAt;
      order.nextExecutionDate = this._calcNextDate(order.nextExecutionDate, order.frequency);
      order.history.push({ action: 'EXECUTED', at: execution.executedAt, paymentId: pmt.id, status: pmt.status });

      return { success: execution.success, payment: pmt, execution };
    } catch (e) {
      const execution = { orderId: order.id, paymentId: null, executedAt: this._nowISO(), success: false, error: e.message };
      this.executions.push(execution);
      order.history.push({ action: 'EXECUTION_FAILED', at: execution.executedAt, error: e.message });
      return { success: false, error: e.message };
    }
  }

  async executeDue(asOfDate) {
    const due = this.getDue(asOfDate);
    const results = [];
    for (const order of due) {
      results.push(await this.execute(order.id));
    }
    return results;
  }

  _calcNextDate(fromDateStr, frequency) {
    const d = new Date(fromDateStr + 'T00:00:00Z');
    switch (frequency) {
      case SOFrequency.WEEKLY: d.setUTCDate(d.getUTCDate() + 7); break;
      case SOFrequency.FORTNIGHTLY: d.setUTCDate(d.getUTCDate() + 14); break;
      case SOFrequency.MONTHLY: d.setUTCMonth(d.getUTCMonth() + 1); break;
      case SOFrequency.QUARTERLY: d.setUTCMonth(d.getUTCMonth() + 3); break;
      case SOFrequency.ANNUAL: d.setUTCFullYear(d.getUTCFullYear() + 1); break;
      default: d.setUTCMonth(d.getUTCMonth() + 1);
    }
    return d.toISOString().split('T')[0];
  }
}


// ============================================================
// PAYMENT ORCHESTRATOR
// ============================================================

class PaymentOrchestrator {
  constructor(ledger, options = {}) {
    this.ledger = ledger;
    this.payments = new Map();
    this.adapters = new Map();
    this.productLookup = options.productLookup || (() => null);
    this._businessDate = options.businessDate || _defaultBusinessDate;

    for (const scheme of Object.values(PaymentScheme)) {
      this.adapters.set(scheme, new PaymentSchemeAdapter(scheme));
    }
  }

  _nowISO() { return this._businessDate.getCurrentDate().toISOString(); }

  // Allow injecting deterministic adapters for testing
  setAdapter(scheme, adapter) {
    this.adapters.set(scheme, adapter);
  }

  // ---- INITIATE: Create payment and place hold ----
  initiatePayment({ sourceAccountId, amount, currency, scheme, beneficiary, reference, metadata = {}, initiatedBy }) {
    const sourceAccount = this.ledger.getAccount(sourceAccountId);
    if (!sourceAccount) throw new Error('Source account not found');

    const product = this.productLookup(sourceAccount);
    const feeConfig = SCHEME_CONFIG[scheme];
    let feeAmount = 0;
    if (feeConfig && product && product.fees) {
      const feeType = this._feeTypeForScheme(scheme);
      const fee = product.fees.find(f => f.type === feeType);
      feeAmount = fee?.amount || 0;
    }

    const payment = {
      id: genId('pmt_'),
      sourceAccountId,
      amount,
      currency,
      scheme,
      direction: PaymentDirection.OUTBOUND,
      beneficiary: { ...beneficiary },
      reference: reference || `PMT-${Date.now()}`,
      status: PaymentStatus.INITIATED,
      statusHistory: [{ status: PaymentStatus.INITIATED, at: this._nowISO(), by: initiatedBy || 'system' }],
      holdId: null,
      settlementTxnId: null,
      feeTxnId: null,
      feeAmount,
      schemeReference: null,
      schemeTransactionId: null,
      rejectionReason: null,
      requiresApproval: false,
      approvedBy: null,
      approvedAt: null,
      approvalThreshold: null,
      returnReason: null,
      returnedAmount: null,
      metadata,
      createdAt: this._nowISO(),
      updatedAt: this._nowISO(),
    };

    try {
      const hold = this.ledger.placeHold({
        accountId: sourceAccountId,
        amount: amount + feeAmount,
        currency,
        reason: `Payment hold: ${scheme} ${reference}`,
        paymentId: payment.id,
      });
      payment.holdId = hold.id;
    } catch (e) {
      payment.status = PaymentStatus.FAILED;
      payment.statusHistory.push({ status: PaymentStatus.FAILED, at: this._nowISO(), reason: e.message });
    }

    this.payments.set(payment.id, payment);
    this.ledger.eventBus.publish({ type: 'PaymentInitiated', entity_id: payment.id, data: { scheme, amount, currency, status: payment.status } });
    return payment;
  }

  // ---- VALIDATE: Run full validation pipeline ----
  validatePayment(paymentId) {
    const payment = this.payments.get(paymentId);
    if (!payment) throw new Error('Payment not found');
    if (payment.status !== PaymentStatus.INITIATED) throw new Error(`Cannot validate: status is ${payment.status}`);

    const sourceAccount = this.ledger.getAccount(payment.sourceAccountId);
    const balance = this.ledger.getBalance(payment.sourceAccountId);
    const product = this.productLookup(sourceAccount);

    // Adjust available balance to account for this payment's own hold (already placed in initiate)
    const adjustedBalance = { ...balance };
    if (payment.holdId) {
      adjustedBalance.available = balance.available + payment.amount + (payment.feeAmount || 0);
    }

    const context = {
      sourceAccount, balance: adjustedBalance, product,
      feeAmount: payment.feeAmount,
      recentPayments: Array.from(this.payments.values()).filter(p => p.sourceAccountId === payment.sourceAccountId && p.id !== payment.id),
    };

    // Check daily limit (exclude this payment — it's already counted in getDailyUsage)
    const dailyUsed = this.getDailyUsage(payment.scheme) - payment.amount;
    const dailyLimit = DAILY_LIMITS[payment.scheme];
    if (dailyLimit && (dailyUsed + payment.amount) > dailyLimit) {
      context.dailyLimitExceeded = true;
    }

    const result = runValidationPipeline(payment, context);

    if (context.dailyLimitExceeded) {
      result.valid = false;
      result.errors.push(`Daily ${payment.scheme} limit exceeded`);
    }

    if (result.valid) {
      const needsApproval = APPROVAL_CONFIG.enabled && APPROVAL_CONFIG.requiresDualAuth(payment.scheme, payment.amount);
      this._transition(payment, PaymentStatus.VALIDATED);

      if (needsApproval) {
        payment.requiresApproval = true;
        payment.approvalThreshold = APPROVAL_CONFIG.getThreshold(payment.scheme);
        this._transition(payment, PaymentStatus.PENDING_APPROVAL);
        this.ledger.eventBus.publish({ type: 'PaymentAwaitingApproval', entity_id: payment.id, data: { scheme: payment.scheme, amount: payment.amount, threshold: payment.approvalThreshold } });
      } else {
        payment.requiresApproval = false;
      }
    } else {
      this._transition(payment, PaymentStatus.FAILED, result.errors.join('; '));
      if (payment.holdId) {
        try { this.ledger.releaseHold(payment.holdId); } catch (e) { /* */ }
      }
    }

    return { ...result, payment, requiresApproval: payment.requiresApproval };
  }

  // ---- APPROVE (dual-auth) ----
  approvePayment(paymentId, approvedBy) {
    const payment = this.payments.get(paymentId);
    if (!payment) throw new Error('Payment not found');
    if (payment.status !== PaymentStatus.PENDING_APPROVAL) throw new Error(`Cannot approve: status is ${payment.status}`);

    const initiator = payment.statusHistory.find(h => h.status === PaymentStatus.INITIATED)?.by;
    if (initiator && initiator === approvedBy) throw new Error('Cannot self-approve: dual authorisation requires a different user');

    payment.approvedBy = approvedBy;
    payment.approvedAt = this._nowISO();
    payment.status = PaymentStatus.VALIDATED;
    payment.statusHistory.push({ status: PaymentStatus.VALIDATED, at: this._nowISO(), by: approvedBy, reason: 'Dual-auth approved' });
    this.ledger.eventBus.publish({ type: 'PaymentApproved', entity_id: payment.id, data: { approvedBy, amount: payment.amount } });
    return payment;
  }

  // ---- REJECT APPROVAL ----
  rejectApproval(paymentId, rejectedBy, reason) {
    const payment = this.payments.get(paymentId);
    if (!payment) throw new Error('Payment not found');
    if (payment.status !== PaymentStatus.PENDING_APPROVAL) throw new Error(`Cannot reject: status is ${payment.status}`);

    this._transition(payment, PaymentStatus.FAILED, `Approval rejected by ${rejectedBy}: ${reason || 'No reason given'}`);
    if (payment.holdId) {
      try { this.ledger.releaseHold(payment.holdId); } catch (e) { /* */ }
    }
    this.ledger.eventBus.publish({ type: 'PaymentApprovalRejected', entity_id: payment.id, data: { rejectedBy, reason } });
    return payment;
  }

  // ---- SUBMIT: Send to scheme adapter ----
  async submitPayment(paymentId, options = {}) {
    const payment = this.payments.get(paymentId);
    if (!payment) throw new Error('Payment not found');
    if (payment.status !== PaymentStatus.VALIDATED && payment.status !== PaymentStatus.PENDING_APPROVAL) throw new Error(`Cannot submit: status is ${payment.status}`);

    if (payment.requiresApproval && !payment.approvedBy && !options.skipApproval) {
      throw new Error('Payment requires dual authorisation before submission');
    }

    if (payment.scheme === PaymentScheme.INTERNAL) {
      return this._settleInternal(payment);
    }

    this._transition(payment, PaymentStatus.SUBMITTED);
    this.ledger.eventBus.publish({ type: 'PaymentSubmitted', entity_id: payment.id, data: { scheme: payment.scheme } });

    const adapter = this.adapters.get(payment.scheme);
    const response = await adapter.submit(payment);
    payment.schemeReference = response.schemeReference;

    if (response.status === 'ACCEPTED') {
      payment.schemeTransactionId = response.schemeTransactionId;
      this._transition(payment, PaymentStatus.ACCEPTED);
      this._settleOutbound(payment);
    } else {
      payment.rejectionReason = response.rejectionReason;
      this._transition(payment, PaymentStatus.REJECTED, response.rejectionReason);
      if (payment.holdId) {
        try { this.ledger.releaseHold(payment.holdId); } catch (e) { /* */ }
      }
    }

    return payment;
  }

  // ---- RECEIVE INBOUND PAYMENT ----
  receiveInboundPayment({ targetAccountId, amount, currency, scheme, sender, reference, schemeTransactionId }) {
    const targetAccount = this.ledger.getAccount(targetAccountId);
    if (!targetAccount) throw new Error('Target account not found');

    const payment = {
      id: genId('pmt_'),
      sourceAccountId: null,
      targetAccountId,
      amount,
      currency,
      scheme,
      direction: PaymentDirection.INBOUND,
      sender: { ...sender },
      reference,
      status: PaymentStatus.ACCEPTED,
      statusHistory: [
        { status: PaymentStatus.INITIATED, at: this._nowISO(), by: 'scheme' },
        { status: PaymentStatus.ACCEPTED, at: this._nowISO(), by: 'scheme' },
      ],
      holdId: null,
      settlementTxnId: null,
      feeAmount: 0,
      schemeReference: `${scheme}-IN-${genId('')}`,
      schemeTransactionId: schemeTransactionId || `SCH-${genId('')}`,
      requiresApproval: false,
      approvedBy: null,
      returnReason: null,
      returnedAmount: null,
      metadata: {},
      createdAt: this._nowISO(),
      updatedAt: this._nowISO(),
    };

    const nostroNum = currency === 'GBP' ? 'INT-NOSTRO-GBP' : 'INT-NOSTRO-USD';
    const nostro = this.ledger.getAccountByNumber(nostroNum);

    const txn = this.ledger.postTransaction({
      type: `${scheme}_INBOUND`,
      reference: payment.reference,
      legs: [
        { accountId: nostro.id, amount, direction: Direction.DEBIT, currency },
        { accountId: targetAccountId, amount, direction: Direction.CREDIT, currency },
      ],
      metadata: { paymentId: payment.id, sender: payment.sender },
      createdBy: 'payment-orchestrator',
    });

    payment.settlementTxnId = txn.id;
    payment.status = PaymentStatus.SETTLED;
    payment.statusHistory.push({ status: PaymentStatus.SETTLED, at: this._nowISO(), by: 'payment-orchestrator' });

    this.payments.set(payment.id, payment);
    this.ledger.eventBus.publish({ type: 'PaymentSettled', entity_id: payment.id, data: { direction: 'INBOUND', amount, currency, scheme, accountId: targetAccountId, senderName: sender?.name, customerId: targetAccount?.customerId || null } });
    return payment;
  }

  // ---- RETURN PAYMENT ----
  returnPayment(paymentId, { reason, returnedAmount, returnedBy }) {
    const payment = this.payments.get(paymentId);
    if (!payment) throw new Error('Payment not found');
    if (payment.status !== PaymentStatus.SETTLED && payment.status !== PaymentStatus.ACCEPTED) {
      throw new Error(`Cannot return: status is ${payment.status}`);
    }

    const amount = returnedAmount || payment.amount;

    if (payment.direction === PaymentDirection.OUTBOUND) {
      const nostroNum = payment.currency === 'GBP' ? 'INT-NOSTRO-GBP' : 'INT-NOSTRO-USD';
      const nostro = this.ledger.getAccountByNumber(nostroNum);
      this.ledger.postTransaction({
        type: `${payment.scheme}_RETURN`,
        reference: `RTN-${payment.reference}`,
        legs: [
          { accountId: nostro.id, amount, direction: Direction.DEBIT, currency: payment.currency },
          { accountId: payment.sourceAccountId, amount, direction: Direction.CREDIT, currency: payment.currency },
        ],
        metadata: { paymentId: payment.id, reason, originalAmount: payment.amount },
        createdBy: returnedBy || 'payment-orchestrator',
      });
    } else {
      const nostroNum = payment.currency === 'GBP' ? 'INT-NOSTRO-GBP' : 'INT-NOSTRO-USD';
      const nostro = this.ledger.getAccountByNumber(nostroNum);
      this.ledger.postTransaction({
        type: `${payment.scheme}_RETURN`,
        reference: `RTN-${payment.reference}`,
        legs: [
          { accountId: payment.targetAccountId, amount, direction: Direction.DEBIT, currency: payment.currency },
          { accountId: nostro.id, amount, direction: Direction.CREDIT, currency: payment.currency },
        ],
        metadata: { paymentId: payment.id, reason },
        createdBy: returnedBy || 'payment-orchestrator',
      });
    }

    payment.returnReason = reason;
    payment.returnedAmount = amount;
    this._transition(payment, PaymentStatus.RETURNED, `Returned: ${reason}`);
    this.ledger.eventBus.publish({ type: 'PaymentReturned', entity_id: payment.id, data: { amount, reason, direction: payment.direction } });
    return payment;
  }

  // ---- INTERNAL SETTLEMENT HELPERS ----

  _settleInternal(payment) {
    this._transition(payment, PaymentStatus.SUBMITTED);

    // Check beneficiary exists BEFORE accepting
    const targetAcc = this._findInternalBeneficiary(payment);
    if (!targetAcc) {
      this._transition(payment, PaymentStatus.FAILED, 'Beneficiary account not found internally');
      if (payment.holdId) try { this.ledger.releaseHold(payment.holdId); } catch (e) { /* */ }
      return payment;
    }

    this._transition(payment, PaymentStatus.ACCEPTED);

    if (payment.holdId) this.ledger.captureHold(payment.holdId);

    const txn = this.ledger.postTransaction({
      type: 'BOOK_TRANSFER',
      reference: payment.reference,
      legs: [
        { accountId: payment.sourceAccountId, amount: payment.amount, direction: Direction.DEBIT, currency: payment.currency },
        { accountId: targetAcc.id, amount: payment.amount, direction: Direction.CREDIT, currency: payment.currency },
      ],
      metadata: { paymentId: payment.id },
      createdBy: 'payment-orchestrator',
    });

    payment.settlementTxnId = txn.id;
    this._transition(payment, PaymentStatus.SETTLED);
    this._chargeFee(payment);
    return payment;
  }

  _settleOutbound(payment) {
    if (payment.holdId) this.ledger.captureHold(payment.holdId);

    const nostroNum = payment.currency === 'GBP' ? 'INT-NOSTRO-GBP' : 'INT-NOSTRO-USD';
    const nostro = this.ledger.getAccountByNumber(nostroNum);

    const txn = this.ledger.postTransaction({
      type: `${payment.scheme}_OUTBOUND`,
      reference: payment.reference,
      legs: [
        { accountId: payment.sourceAccountId, amount: payment.amount, direction: Direction.DEBIT, currency: payment.currency },
        { accountId: nostro.id, amount: payment.amount, direction: Direction.CREDIT, currency: payment.currency },
      ],
      metadata: { paymentId: payment.id, beneficiary: payment.beneficiary, schemeTransactionId: payment.schemeTransactionId },
      createdBy: 'payment-orchestrator',
    });

    payment.settlementTxnId = txn.id;
    this._transition(payment, PaymentStatus.SETTLED);
    this._chargeFee(payment);
    const _srcAcct = this.ledger.getAccount(payment.sourceAccountId);
    this.ledger.eventBus.publish({ type: 'PaymentSettled', entity_id: payment.id, data: { direction: 'OUTBOUND', amount: payment.amount, scheme: payment.scheme, sourceAccountId: payment.sourceAccountId, beneficiaryName: payment.beneficiary?.name, customerId: _srcAcct?.customerId || null } });
  }

  _chargeFee(payment) {
    if (!payment.feeAmount || payment.feeAmount <= 0) return;
    try {
      const feeIncome = this.ledger.getAccountByNumber('INT-FEE-INCOME');
      const txn = this.ledger.postTransaction({
        type: 'PAYMENT_FEE',
        reference: `FEE-${payment.reference}`,
        legs: [
          { accountId: payment.sourceAccountId, amount: payment.feeAmount, direction: Direction.DEBIT, currency: payment.currency },
          { accountId: feeIncome.id, amount: payment.feeAmount, direction: Direction.CREDIT, currency: payment.currency },
        ],
        metadata: { paymentId: payment.id },
        createdBy: 'payment-orchestrator',
      });
      payment.feeTxnId = txn.id;
    } catch (e) { /* fee charge failed — log but don't fail */ }
  }

  _findInternalBeneficiary(payment) {
    const accNum = payment.beneficiary?.accountNumber;
    if (!accNum) return null;
    try { return this.ledger.getAccountByNumber(accNum); } catch { return null; }
  }

  _feeTypeForScheme(scheme) {
    const map = { FPS: 'PAYMENT_FPS', CHAPS: 'PAYMENT_CHAPS', SWIFT: 'PAYMENT_INTL' };
    return map[scheme] || null;
  }

  _transition(payment, newStatus, reason) {
    if (!(PAYMENT_TRANSITIONS[payment.status] || []).includes(newStatus)) {
      throw new Error(`Invalid payment transition: ${payment.status} → ${newStatus}`);
    }
    payment.status = newStatus;
    payment.updatedAt = this._nowISO();
    payment.statusHistory.push({ status: newStatus, at: payment.updatedAt, by: 'orchestrator', reason });
    this.ledger.eventBus.publish({
      type: 'PaymentStatusChanged', entity_id: payment.id,
      data: { from: payment.statusHistory[payment.statusHistory.length - 2]?.status, to: newStatus, reason },
    });
  }

  // ---- QUERIES ----

  getPayment(id) { return this.payments.get(id); }
  getAllPayments() { return Array.from(this.payments.values()); }
  getPaymentsByAccount(accountId) { return this.getAllPayments().filter(p => p.sourceAccountId === accountId || p.targetAccountId === accountId); }
  getPendingApprovals() { return this.getAllPayments().filter(p => p.status === PaymentStatus.PENDING_APPROVAL); }

  getDailyUsage(scheme, date) {
    const today = date || this._nowISO().split('T')[0];
    return this.getAllPayments()
      .filter(p => p.direction === PaymentDirection.OUTBOUND && p.scheme === scheme
        && p.createdAt.startsWith(today)
        && p.status !== PaymentStatus.FAILED && p.status !== PaymentStatus.REJECTED)
      .reduce((sum, p) => sum + p.amount, 0);
  }

  getDailyLimitSummary() {
    const today = this._nowISO().split('T')[0];
    return Object.entries(DAILY_LIMITS).map(([scheme, limit]) => ({
      scheme,
      limit,
      used: this.getDailyUsage(scheme, today),
      remaining: limit ? limit - this.getDailyUsage(scheme, today) : null,
      pct: limit ? (this.getDailyUsage(scheme, today) / limit) * 100 : 0,
    }));
  }

  getNostroSummary() {
    const nostros = [];
    for (const ccy of ['GBP', 'USD']) {
      try {
        const acc = this.ledger.getAccountByNumber(`INT-NOSTRO-${ccy}`);
        const bal = this.ledger.getBalance(acc.id);
        const settled = this.getAllPayments().filter(p => p.status === PaymentStatus.SETTLED && p.currency === ccy);
        const outbound = settled.filter(p => p.direction === PaymentDirection.OUTBOUND);
        const inbound = settled.filter(p => p.direction === PaymentDirection.INBOUND);
        const inFlight = this.getAllPayments().filter(p =>
          p.currency === ccy && ![PaymentStatus.SETTLED, PaymentStatus.FAILED, PaymentStatus.REJECTED, PaymentStatus.RETURNED].includes(p.status)
        );
        nostros.push({
          currency: ccy, accountId: acc.id, accountNumber: acc.accountNumber,
          balance: bal.posted, available: bal.available,
          totalOutbound: outbound.reduce((s, p) => s + p.amount, 0),
          totalInbound: inbound.reduce((s, p) => s + p.amount, 0),
          outboundCount: outbound.length, inboundCount: inbound.length,
          inFlightCount: inFlight.length,
          inFlightAmount: inFlight.reduce((s, p) => s + p.amount, 0),
        });
      } catch (e) { /* nostro not found */ }
    }
    return nostros;
  }
}


// ============================================================
// EXPORTS
// ============================================================

export {
  PaymentStatus, PaymentDirection, PaymentScheme,
  PAYMENT_TRANSITIONS, SCHEME_CONFIG, APPROVAL_CONFIG, DAILY_LIMITS,
  SOFrequency, SOStatus,
  Validators, runValidationPipeline,
  PaymentSchemeAdapter, PaymentOrchestrator,
  BeneficiaryRegistry, StandingOrderEngine,
};
